/**
 * Format a number as currency (USD by default)
 */
export function formatCurrency(
  amount: number,
  currency = 'USD',
  locale = 'en-US',
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Calculate discount percentage between original and current price
 */
export function calcDiscountPct(original: number, current: number): number {
  if (original <= 0) return 0;
  return Math.round(((original - current) / original) * 100);
}

/**
 * Slugify a string (e.g. "Panel Solar 400W" → "panel-solar-400w")
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

/**
 * Truncate a string to a max length, appending ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
}

/**
 * Clamp a number between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Generate a simple unique ID (not cryptographic)
 */
export function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Delay execution (useful for debouncing or testing)
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Group an array of objects by a key
 */
export function groupBy<T>(
  array: T[],
  key: keyof T,
): Record<string, T[]> {
  return array.reduce<Record<string, T[]>>((acc, item) => {
    const groupKey = String(item[key]);
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(item);
    return acc;
  }, {});
}

/**
 * Check if a value is a non-empty string
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Posición al azar de cada producto, sorteada una vez por visita. Así el orden
 * cambia cada vez que se abre la página, pero no salta mientras el cliente
 * mira (la lista se vuelve a pedir al volver a la pestaña).
 */
const rangoAleatorio = new Map<string, number>();

/** Devuelve la lista en orden aleatorio, estable durante la visita. */
export function ordenAleatorio<T extends { id: string }>(lista: T[]): T[] {
  const rango = (id: string) => {
    let r = rangoAleatorio.get(id);
    if (r === undefined) {
      r = Math.random();
      rangoAleatorio.set(id, r);
    }
    return r;
  };
  return [...lista].sort((a, b) => rango(a.id) - rango(b.id));
}

/** Pone primero los productos en oferta, sin cambiar el orden dentro de cada grupo. */
export function ofertasPrimero<T extends { price: number; originalPrice?: number }>(lista: T[]): T[] {
  const enOferta = (p: T) => (p.originalPrice ?? 0) > p.price;
  return [...lista.filter(enOferta), ...lista.filter((p) => !enOferta(p))];
}
